import React, { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import EmojiPicker from "emoji-picker-react";

const TABLET_MIN = 768;
const TABLET_MAX = 890;
const PICKER_HEIGHT = 340;
const PICKER_HEIGHT_TABLET = 300;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getPickerWidth = (viewportWidth, containerWidth) => {
  if (viewportWidth >= TABLET_MIN && viewportWidth <= TABLET_MAX) {
    return Math.min(320, containerWidth - 16, viewportWidth - 24);
  }
  if (viewportWidth < TABLET_MIN) {
    return Math.min(320, viewportWidth - 24);
  }
  return Math.min(350, containerWidth - 16);
};

export default function EmojiPickerPopover({
  open,
  anchorRef,
  containerRef,
  pickerRef,
  onEmojiClick,
}) {
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const anchor = anchorRef?.current;
      if (!anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const containerRect = containerRef?.current?.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const isTablet = viewportWidth >= TABLET_MIN && viewportWidth <= TABLET_MAX;
      const boundsWidth = containerRect?.width || viewportWidth;
      const pickerWidth = getPickerWidth(viewportWidth, boundsWidth);
      const pickerHeight = isTablet ? PICKER_HEIGHT_TABLET : PICKER_HEIGHT;
      const edgeGap = 12;

      let left;
      if (isTablet && containerRect) {
        left = containerRect.left + (containerRect.width - pickerWidth) / 2;
      } else {
        left = anchorRect.right - pickerWidth;
      }

      if (containerRect) {
        left = clamp(
          left,
          containerRect.left + edgeGap,
          containerRect.right - pickerWidth - edgeGap
        );
      } else {
        left = clamp(left, edgeGap, viewportWidth - pickerWidth - edgeGap);
      }

      const top = anchorRect.top - pickerHeight - 10;

      setPosition({
        top: Math.max(edgeGap, top),
        left,
        width: pickerWidth,
        height: pickerHeight,
      });
    };

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, anchorRef, containerRef]);

  if (!open || !position || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={pickerRef}
      className="emoji-picker-popover fixed z-[9999] overflow-hidden rounded-2xl border border-[#ECE6FF] bg-white shadow-[0_20px_60px_rgba(35,16,83,0.22)]"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
      }}
    >
      <EmojiPicker
        width={position.width}
        height={position.height}
        onEmojiClick={onEmojiClick}
      />
    </div>,
    document.body
  );
}
